/**
 * Mission Settings Panel Component
 *
 * Modal dialog for configuring mission-wide settings
 * including altitude, speed, vehicle type, and firmware
 */

import { useState, useEffect } from 'react';
import { X, Settings as SettingsIcon } from 'lucide-react';
import { useMissionStore } from '@/stores/useMissionStore';
import { VehicleType } from '@/features/mission/types/mission.types';
import { FirmwareType } from '@/features/mission/types/settings.types';
import { MISSION_DEFAULTS } from '@/constants/mission-defaults';
import { toast } from 'sonner';

interface MissionSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MissionSettingsPanel({ isOpen, onClose }: MissionSettingsPanelProps) {
  const currentMission = useMissionStore((state) => state.currentMission);
  const updateMissionSettings = useMissionStore((state) => state.updateMissionSettings);

  // Local state for form inputs
  const [defaultAltitude, setDefaultAltitude] = useState<number>(MISSION_DEFAULTS.defaultAltitude);
  const [defaultSpeed, setDefaultSpeed] = useState<number>(MISSION_DEFAULTS.defaultSpeed);
  const [cruiseSpeed, setCruiseSpeed] = useState<number>(MISSION_DEFAULTS.cruiseSpeed);
  const [vehicleType, setVehicleType] = useState<VehicleType>(MISSION_DEFAULTS.defaultVehicleType);
  const [firmwareType, setFirmwareType] = useState<FirmwareType>(FirmwareType.ARDUPILOT);

  // Initialize form values from current mission
  useEffect(() => {
    if (currentMission && isOpen) {
      setDefaultAltitude(currentMission.defaultAltitude);
      setDefaultSpeed(currentMission.defaultSpeed);
      setCruiseSpeed(currentMission.cruiseSpeed);
      setVehicleType(currentMission.vehicleType);
      setFirmwareType(currentMission.settings?.firmwareType ?? FirmwareType.ARDUPILOT);
    }
  }, [currentMission, isOpen]);

  const handleApply = () => {
    if (!currentMission) {
      toast.error('No active mission');
      return;
    }

    // Validate inputs
    if (defaultAltitude < MISSION_DEFAULTS.minAltitude || defaultAltitude > MISSION_DEFAULTS.maxAltitude) {
      toast.error(`Altitude must be between ${MISSION_DEFAULTS.minAltitude}m and ${MISSION_DEFAULTS.maxAltitude}m`);
      return;
    }

    if (defaultSpeed <= 0 || defaultSpeed > MISSION_DEFAULTS.maxSpeed) {
      toast.error(`Speed must be between 1 and ${MISSION_DEFAULTS.maxSpeed} m/s`);
      return;
    }

    // Update mission settings
    updateMissionSettings({
      defaultAltitude,
      defaultSpeed,
      cruiseSpeed,
      maxSpeed: MISSION_DEFAULTS.maxSpeed,
      vehicleType,
      firmwareType,
    });

    toast.success('Mission settings updated');
    onClose();
  };

  const handleCancel = () => {
    // Reset to current mission values
    if (currentMission) {
      setDefaultAltitude(currentMission.defaultAltitude);
      setDefaultSpeed(currentMission.defaultSpeed);
      setCruiseSpeed(currentMission.cruiseSpeed);
      setVehicleType(currentMission.vehicleType);
      setFirmwareType(currentMission.settings?.firmwareType ?? FirmwareType.ARDUPILOT);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
        <div
          className="bg-slate-900 rounded-lg border border-slate-700 shadow-2xl w-full max-w-2xl mx-4 pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <SettingsIcon className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-semibold text-white">Mission Settings</h2>
            </div>
            <button
              onClick={handleCancel}
              className="text-slate-400 hover:text-white transition-colors p-1"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Flight Parameters Section */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-4">Flight Parameters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Default Altitude */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Default Altitude
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={defaultAltitude}
                      onChange={(e) => setDefaultAltitude(Number(e.target.value))}
                      min={MISSION_DEFAULTS.minAltitude}
                      max={MISSION_DEFAULTS.maxAltitude}
                      step={1}
                      className="w-full bg-slate-800 text-white rounded-md border border-slate-600 px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                      m
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Range: {MISSION_DEFAULTS.minAltitude}-{MISSION_DEFAULTS.maxAltitude}m
                  </p>
                </div>

                {/* Default Speed */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Default Speed
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={defaultSpeed}
                      onChange={(e) => setDefaultSpeed(Number(e.target.value))}
                      min={1}
                      max={MISSION_DEFAULTS.maxSpeed}
                      step={0.5}
                      className="w-full bg-slate-800 text-white rounded-md border border-slate-600 px-3 py-2 pr-14 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                      m/s
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Range: 1-{MISSION_DEFAULTS.maxSpeed} m/s
                  </p>
                </div>

                {/* Cruise Speed */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Cruise Speed
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={cruiseSpeed}
                      onChange={(e) => setCruiseSpeed(Number(e.target.value))}
                      min={1}
                      max={MISSION_DEFAULTS.maxSpeed}
                      step={0.5}
                      className="w-full bg-slate-800 text-white rounded-md border border-slate-600 px-3 py-2 pr-14 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                      m/s
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Speed for long segments
                  </p>
                </div>
              </div>
            </div>

            {/* Vehicle Configuration Section */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-4">Vehicle Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Vehicle Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Vehicle Type
                  </label>
                  <select
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value as VehicleType)}
                    className="w-full bg-slate-800 text-white rounded-md border border-slate-600 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={VehicleType.COPTER}>Multicopter</option>
                    <option value={VehicleType.PLANE}>Fixed Wing</option>
                    <option value={VehicleType.VTOL}>VTOL</option>
                    <option value={VehicleType.ROVER}>Rover</option>
                    <option value={VehicleType.SUB}>Submarine</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Type of vehicle for this mission
                  </p>
                </div>

                {/* Firmware Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Firmware Type
                  </label>
                  <select
                    value={firmwareType}
                    onChange={(e) => setFirmwareType(e.target.value as FirmwareType)}
                    className="w-full bg-slate-800 text-white rounded-md border border-slate-600 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={FirmwareType.ARDUPILOT}>ArduPilot</option>
                    <option value={FirmwareType.PX4}>PX4</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Firmware running on vehicle
                  </p>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-900/20 border border-blue-700/50 rounded-md p-4">
              <p className="text-sm text-blue-200">
                <span className="font-semibold">Note:</span> These settings will be applied to all new waypoints added to this mission. Existing waypoints will not be affected.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
            >
              Apply Settings
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
